import type { ReviewItem } from '@app/shared/types/sf-data'

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { getReviewItemKey } from '@app/application/workflows/screens/workflow-review/utils'

type ReviewItemNoStatus = Omit<ReviewItem, 'workflowReviewStatusData'>

export const useWorfklowReviewItems = defineStore(
  'workflow-review-items',
  () => {
    const currentReviewItemIndex = ref<number>(0)
    const reviewItemsOrder = ref<Array<string>>([])
    const reviewItems = ref<Record<string, ReviewItemNoStatus>>({})

    const currentReviewItem = computed<ReviewItemNoStatus | undefined>(
      () =>
        reviewItems.value[reviewItemsOrder.value[currentReviewItemIndex.value]]
    )

    const isEmpty = computed<boolean>(() => reviewItemsOrder.value.length === 0)

    const itemLength = computed<number>(() => reviewItemsOrder.value.length)

    const reviewItemsAsArray = computed<Array<ReviewItemNoStatus>>(() =>
      reviewItemsOrder.value.map((key) => reviewItems.value[key])
    )

    function receiveReviewItems(items: Array<ReviewItem>): void {
      const { focusedIndex, data, order } = items.reduce(
        (acc, item, index) => {
          const key = getReviewItemKey(item)
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { workflowReviewStatusData, ...rest } = item
          const { bodyElement, compareBodyElement, ...toBeFrozen } = rest

          acc.data[key] = {
            ...Object.freeze(toBeFrozen),
            bodyElement,
            compareBodyElement,
          }

          acc.order.push(key)

          if (!acc.focusedIndex && item.focused) {
            acc.focusedIndex = index
          }

          return acc
        },
        {
          focusedIndex: 0,
          order: [],
          data: {},
        } as {
          focusedIndex: number
          order: Array<string>
          data: Record<string, ReviewItemNoStatus>
        }
      )

      currentReviewItemIndex.value = focusedIndex
      reviewItemsOrder.value = order
      reviewItems.value = data
    }

    function updateCurrentReviewItemIndex(index: number): void {
      currentReviewItemIndex.value = index
    }

    function updateReviewItem({ item }: { item: ReviewItem }): void {
      const key = getReviewItemKey(item)
      const existingItem = reviewItems.value[key]

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { workflowReviewStatusData, ...rest } = item

      const frozenRest = Object.freeze(rest)

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { bodyElement, compareBodyElement, ...itemWithoutBody } = frozenRest

      // if the rendered Liquid body is already there, don't override it
      const itemToMerge = existingItem.bodyElement
        ? itemWithoutBody
        : frozenRest

      reviewItems.value[key] = {
        ...existingItem,
        ...itemToMerge,
      }
    }

    function updateReviewItemHTML({
      itemClass,
      itemId,
      bodyElement,
      compareBodyElement,
      reconciliationStatusIcon,
      hasBodyElement,
      hasCompareBodyElement,
      diffLimitReached,
      activeAIProposal,
      error,
    }: {
      itemClass: string
      itemId: number
      bodyElement: string
      compareBodyElement: string
      reconciliationStatusIcon: string
      hasBodyElement: boolean
      hasCompareBodyElement: boolean
      diffLimitReached: boolean
      activeAIProposal: boolean
      error: string
    }): void {
      const key = `${itemClass}:${itemId}`

      if (!reviewItems.value[key]) {
        return
      }

      reviewItems.value[key] = {
        ...reviewItems.value[key],
        hasBodyElement,
        hasCompareBodyElement,
        bodyElement,
        compareBodyElement,
        reconciliationStatusIcon,
        diffLimitReached,
        activeAIProposal,
        error,
      }
    }

    return {
      currentIndex: currentReviewItemIndex,
      currentReviewItem,
      isEmpty,
      itemLength,
      receiveReviewItems,
      reviewItems: reviewItemsAsArray,
      updateCurrentReviewItemIndex,
      updateReviewItem,
      updateReviewItemHTML,
    }
  }
)
